<div class="my-4 ml-4 flex h-10 items-center justify-between">
  <div></div>
  <div class="typo-h5-semibold text-text-secondary">
    {{ sidebarTitle() | translate }}
  </div>
  <button class="flex items-center text-primary-blue" (click)="onCloseClick()">
    <svg-icon size="md" key="px-24-x"></svg-icon>
  </button>
</div>
<div class="flex h-full flex-col overflow-auto" #scrollArea>
  <lib-patient-selector
    [placeholder]="'schedule_select_patient_placeholder' | translate"
    [noResultLabel]="'collab_new_collab_modal_collaboration_no_result_label' | translate"
    [disabled]="!siteId()"
    [selectedPatient]="isEditedAppointmentPreRegistration() ? editedAppointmentPreRegistrationFakePatient() : patient()"
    [minimumSearchLength]="1"
    [isShowcaseMode]="isShowcase()"
    [readonly]="isEditAppointment()"
    [closeOnSelect]="true"
    [searchInEmails]="false"
    (itemSelected)="onPatientSelect($event)"
  />

  <p class="typo-h4-bold mb-4 mt-8 uppercase">{{ 'schedule_details_appointment_details' | translate }}</p>

  <div class="flex flex-col gap-2">
    <lib-select-reason
      #reasonSelector
      [dropdownItems]="reasonOptions()"
      [defaultValue]="selectedReasons()"
      [reasonsLoading]="isReasonsDataLoading()"
      [noResultLabel]="'collab_new_collab_modal_collaboration_no_result_label' | translate"
      [prefixIcon]="'px-24-search'"
      [required]="isOpenDental()"
      [submitAttempted]="reasonSubmitAttempted()"
      (itemSelected)="onSelectReason($event)"
      (itemDeselected)="onDeselectReason($event)"
    />
    <div class="flex">
      <div class="flex flex-1"></div>
      <div class="w-[250px] ps-3">
        <button class="typo-h5-semibold w-fit text-primary-blue" (click)="openReasonEditorModal({ selectedReasons: selectedReasons() })">
          {{ 'schedule_browse_reason' | translate }}
        </button>
      </div>
    </div>

    <lib-date-time-selector
      [appointmentDate]="appointmentDate()"
      [startTime]="startTime()"
      [endTime]="endTime()"
      [appointmentLength]="appointmentLength()"
      [timeBlock]="timeBlock()"
      (startTimeSet)="onStartTimeSet($event)"
      (endTimeSet)="onEndTimeSet($event)"
      (dateSelected)="onDateSelect($event)"
    />
    <div class="ms-12 flex items-center justify-end">
      <div class="flex w-[250px] items-center gap-3">
        <lib-dropdown
          #appointmentLengthDropdown
          class="w-[100px] rounded-2xl border"
          [ngClass]="{
            'border-primary-blue': appointmentLengthDropdown.dropdownOpen(),
            'border-states-white': !appointmentLengthDropdown.dropdownOpen()
          }"
          [overlayMaxHeight]="timeOverlayHeight"
          [overlayWidth]="timeOverlayWidth"
          [overlayPositionStrategy]="overlayPositionStrategy"
          [hasMinWidth]="false"
          [options]="appointmentLengths"
          [value]="appointmentLength()"
          [showDropdownIcon]="false"
          (valueChange)="appointmentLengthChange($event)"
          [border]="false"
        />
        <button class="typo-h5-semibold w-fit text-primary-blue" (click)="openTimePatternEditorModal({ timePatterns: timePatterns() })">
          {{ 'schedule_add_time_pattern' | translate }}
        </button>
      </div>
    </div>

    <div class="flex items-center justify-between">
      <div class="typo-h4-light text-text-tertiary">{{ 'schedule_details_appointment_provider' | translate }}</div>
      <lib-select-provider
        class="w-[250px]"
        [defaultValue]="provider()"
        [noResultLabel]="'schedule_details_appointment_no_providers' | translate"
        (itemSelected)="onProviderOptionChange($event)"
      />
    </div>

    @if (isEditAppointment() && secondaryProvider()) {
      <div class="flex items-center justify-between gap-1">
        <div class="typo-h4-light text-text-tertiary">{{ 'schedule_details_appointment_secondary_provider' | translate }}</div>
        <div class="typo-h4-medium w-[250px] px-3 py-2 text-text-light">{{ secondaryProviderDisplay() }}</div>
      </div>
    }

    <div class="flex items-center justify-between gap-1">
      <div class="typo-h4-light text-text-tertiary">{{ 'schedule_details_appointment_operatory' | translate }}</div>
      <lib-dropdown
        #operatoryDropdown
        class="w-[250px] rounded-2xl border"
        [options]="allOperatoryOptions()"
        [value]="operatory()!"
        [compareWith]="operatoryCompareFn"
        (valueChange)="onOperatoryOptionChange($event)"
        [ngClass]="{
          'border-primary-blue': operatoryDropdown.dropdownOpen(),
          'border-states-white': !operatoryDropdown.dropdownOpen()
        }"
        [border]="false"
      />
    </div>

    <div class="flex items-center justify-between gap-1">
      <div class="typo-h4-light text-text-tertiary">{{ 'schedule_details_appointment_status' | translate }}</div>
      <lib-dropdown
        #appointmentStatusDropdown
        class="w-[250px] rounded-2xl border"
        [options]="appointmentStatusOptions()"
        [value]="selectedAppointmentStatus()"
        [disabled]="isReasonsDataLoading()"
        (valueChange)="onAppointmentStatusOptionChange($event)"
        [ngClass]="{
          'border-primary-blue': appointmentStatusDropdown.dropdownOpen(),
          'border-states-white': !appointmentStatusDropdown.dropdownOpen()
        }"
        [border]="false"
      >
        <ng-template libDropdownItemPrefix let-item let-prefixData="prefixData">
          @if (prefixData?.statusCode) {
            <div
              class="typo-h5-semibold flex max-h-[16px] items-center gap-1 rounded px-2 py-1"
              [ngClass]="{
                'border-[1px] border-solid border-text-extra-light': prefixData.backgroundColor === '#ffffff'
              }"
              [style.background-color]="prefixData.backgroundColor"
              [style.color]="prefixData.textColor"
              [attr.aria-label]="'Status: ' + item.label"
              role="status"
            >
              <p class="typo-h8-bold whitespace-nowrap">
                {{ prefixData.statusCode }}
              </p>
            </div>
          }
        </ng-template>
      </lib-dropdown>
    </div>

    <div class="mt-2 flex items-center justify-between gap-1">
      <div class="typo-h4-light self-start text-text-tertiary">{{ 'schedule_details_appointment_notes' | translate }}</div>
      <div
        #voiceTypingTextFieldWrapper
        class="group ml-12 flex max-w-[250px] flex-grow flex-col justify-between gap-2 overflow-y-auto rounded-2xl border bg-background-white px-4 py-2 focus-within:py-4"
        [ngClass]="{
          'border-states-red focus-within:border-states-red': isNotesOverMaxLength(),
          'border-text-extra-light focus-within:border-primary-blue': !isNotesOverMaxLength()
        }"
        (click)="onNotesClick()"
        (focusout)="onNotesFocusOut($event, voiceTypingTextFieldWrapper)"
      >
        <lib-voice-typing-text-field
          class="w-full overflow-auto"
          #voiceTypingTextField
          [value]="notes()"
          [lastFocusedCursorOffset]="lastFocusedCursorOffset()"
          (lastFocusedCursorOffsetChange)="updateLastFocusedCursorOffset($event)"
          [transcriptionFormControl]="transcriptionControl"
          [dictationFinalized$]="dictationFinalized$"
          [transcriptionStatus$]="transcriptionStatus$"
          [isRecording]="transcriptionStatus() === 'recording'"
          [isRecordingLoading]="transcriptionStatus() === 'loading'"
          [placeholder]="'schedule_details_appointment_notes_input_placeholder' | translate"
          [lineBreakOnShiftEnter]="true"
          [showPlaceholderIcon]="false"
          [isInputFocused]="isNotesFocused()"
          [showTranscriptionMarker]="false"
          [showContextMenu]="true"
          (manualTypeKeyUp)="onNotesChange($event)"
          (recordingLoading)="onRecordingLoading($event)"
          (recordingStopped)="onRecordingStopped($event)"
        ></lib-voice-typing-text-field>

        <div
          [ngClass]="{
            'flex items-center justify-between gap-2': !isNotesInputEmpty(),
            'hidden items-center justify-between gap-2 group-focus-within:flex':
              isNotesInputEmpty() && !isNotesFocused() && transcriptionStatus() !== 'recording'
          }"
        >
          <div class="flex gap-2">
            <div class="relative">
              <button
                tabindex="0"
                class="transition-[min-width, background-color] group flex h-full min-h-[32px] min-w-[32px] items-center justify-center rounded-xl bg-background-white p-1 text-states-purple duration-200 ease-in-out"
                (click)="toggleDictationClick()"
                [ngClass]="{
                  'min-w-[169px] bg-states-purple shadow-[0px_4px_24px_0px] shadow-states-purple/30': transcriptionStatus() === 'recording'
                }"
              >
                @switch (transcriptionStatus()) {
                  @case ('idle') {
                    <svg-icon
                      [libUiTooltip]="startVoiceTypingTooltip"
                      [tooltipPosition]="'top'"
                      class=""
                      currentColor
                      key="px-24-voice-v-2"
                      size="md"
                    ></svg-icon>
                  }
                  @case ('loading') {
                    <ng-lottie class="h-4 w-4" currentcolor [options]="lottieOptions"></ng-lottie>
                  }
                  @case ('recording') {
                    <svg-icon
                      [libUiTooltip]="stopVoiceTypingTooltip"
                      [tooltipPosition]="'top'"
                      key="px-40-stop"
                      class="text-text-white"
                      size="md"
                    ></svg-icon>
                  }
                }
              </button>
            </div>
          </div>
        </div>
      </div>

      <ng-template #startVoiceTypingTooltip>
        <div class="text-h7">Voice Typing</div>
      </ng-template>
      <ng-template #stopVoiceTypingTooltip>
        <div class="text-h7">Stop Voice Typing</div>
      </ng-template>
    </div>

    @if (isNotesOverMaxLength()) {
      <div class="mt-1 flex justify-end">
        <div class="typo-h7-light max-w-[250px] text-states-red">
          {{ 'schedule_details_appointment_notes_max_length_error' | translate }}
        </div>
      </div>
    }

    <!-- TODO: Temporary client requirement to save tentative appointment only in showcase -->
    @if (isEditAppointment() && isShowcase()) {
      <form [formGroup]="tentativeForm" class="mt-3 flex items-center gap-2">
        <lib-checkbox formControlName="isTentative"></lib-checkbox>
        <span class="typo-h4-light">{{ 'schedule_save_as_tentative_checkbox' | translate }}</span>
      </form>
    }
  </div>
</div>

@if (errorMessage(); as errorMessage) {
  <div class="rounded-3xl bg-states-white">
    <div class="rounded-3xl border-[1.5px] border-states-red/40 bg-states-red/25 px-[14px] py-[10px]">
      <div class="flex items-center gap-3">
        <svg-icon size="md" class="min-w-[24px] rounded-full bg-states-red text-text-white" key="px-24-warning-2"></svg-icon>
        <div class="flex flex-col gap-1">
          <h4 class="typo-h4-medium">
            {{ errorMessage.title }}
          </h4>
          <h4 class="typo-h5-book font-poppins">
            {{ errorMessage.message }}
          </h4>
        </div>
      </div>
    </div>
  </div>
}

<div class="sticky bottom-0 left-0 z-10 flex justify-end gap-6 bg-background-white py-3">
  @if (isEditAppointment()) {
    <button (click)="onDeleteClick()" [disabled]="saveInProgress()" class="btn-text-warn">
      {{ 'home_right_side_bar_delete_button' | translate }}
    </button>
    <button (click)="onSaveChangesClick()" class="btn-primary w-38 justify-center" [disabled]="saveInProgress() || isNotesOverMaxLength()">
      @if (saveInProgress()) {
        <ng-lottie [options]="lottieOptions" height="18px" width=""></ng-lottie>
      } @else {
        {{ 'home_right_side_bar_save_changes_button' | translate }}
      }
    </button>
  } @else {
    <!-- TODO: Temporary client requirement to save tentative appointment only in showcase -->
    @if (isShowcase()) {
      <button
        (click)="onSaveTentativeClick()"
        class="btn-text"
        [disabled]="isNotesOverMaxLength()"
        [ffdPushTagOnClick]="{
          event: TagEvent.ScheduleAppointmentTentative
        }"
      >
        {{ 'home_right_side_bar_tentative_button' | translate }}
      </button>
    }
    <button
      (click)="onCreateClick()"
      [disabled]="isReasonsDataLoading() || saveInProgress() || isNotesOverMaxLength()"
      class="btn-primary w-24 justify-center"
      [ffdPushTagOnClick]="{
        event: TagEvent.ScheduleAppointmentCreated
      }"
    >
      @if (saveInProgress()) {
        <ng-lottie [options]="lottieOptions" height="18px" width=""></ng-lottie>
      } @else {
        {{ 'home_right_side_bar_create_button' | translate }}
      }
    </button>
  }
</div>
